import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Paperclip, X, Loader2, Send } from "lucide-react";

export function SendEmailCard({ presetTo }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (presetTo) setTo(presetTo); }, [presetTo]);

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) { toast.error("Mete yon imèl valab"); return; }
    if (!message.trim() && files.length === 0) { toast.error("Mete yon mesaj oswa omwen yon dokiman"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("to", to.trim());
      fd.append("subject", subject.trim() || "Yon mesaj soti nan Smart FX");
      fd.append("message", message);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/send-email", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imèl voye bay ${data.to}${data.attachments ? ` ak ${data.attachments} atachman` : ""}`);
      setSubject(""); setMessage(""); setFiles([]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="border border-[#222] bg-[#0F0F0F] p-5 space-y-3" data-testid="send-email-card">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-[#1C8CFF]" />
        <h3 className="font-heading font-semibold text-sm">Voye Dokiman pa Imèl</h3>
      </div>
      <p className="text-[11px] text-[#888] leading-relaxed">Voye dokiman oswa foto bay nenpòt moun pa imèl. Ekri imèl la (oswa chèche yon manm anwo epi peze "Voye Dokiman"), atache fichye yo, epi voye.</p>

      <Input data-testid="send-email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
        placeholder="Imèl moun k ap resevwa a *" className="bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
      <Input data-testid="send-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
        placeholder="Sijè (opsyonèl)" className="bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
      <Textarea data-testid="send-email-message" value={message} onChange={(e) => setMessage(e.target.value)}
        placeholder="Mesaj ou…" rows={4} className="bg-[#0A0A0A] border-[#222] rounded-sm" />

      <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
        onChange={addFiles} className="hidden" data-testid="send-email-file-input" />
      <Button type="button" variant="ghost" onClick={() => fileRef.current?.click()} data-testid="send-email-attach"
        className="h-10 rounded-sm border border-[#333] text-[#8fb4e6] hover:bg-[#1C8CFF]/10 w-full justify-center">
        <Paperclip className="mr-1.5 h-4 w-4" /> Atache Dokiman / Foto
      </Button>

      {files.length > 0 && (
        <div className="space-y-1.5" data-testid="send-email-file-list">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#222] rounded-sm px-3 py-2 text-xs">
              <Paperclip className="h-3 w-3 text-[#5f7ba6] shrink-0" />
              <span className="truncate flex-1">{f.name}</span>
              <span className="text-[#666] shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
              <button onClick={() => removeFile(i)} data-testid={`send-email-remove-${i}`} className="text-[#FF3B30] hover:text-[#ff5f57] shrink-0"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <Button onClick={send} disabled={sending} data-testid="send-email-submit"
        className="w-full h-11 rounded-sm bg-[#1C8CFF] text-white hover:bg-[#1C8CFF]/90 font-semibold">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Voye Imèl</>}
      </Button>
    </div>
  );
}
